export type SidebarMode = 'assistant' | 'workspace';

const STORAGE_PREFIX = 'shahagent.sidebarMode.v1.';

const ASSISTANT_PREFIXES = [
  '/conversations',
  '/calls',
  '/ai',
  '/knowledge',
  '/settings/ai',
  '/automations',
  '/admin/connectors',
  '/setup',
];

const WORKSPACE_PREFIXES = [
  '/dashboard',
  '/contacts',
  '/owners',
  '/assets',
  '/pets',
  '/products',
  '/services',
  '/invoices',
  '/payments',
  '/calendar',
  '/rostering',
  '/field-operations',
  '/consultations',
  '/appointments',
  '/tasks',
  '/reminder-engine',
];

const matchesPrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export const sidebarModeForPath = (pathname: string): SidebarMode | null => {
  if (ASSISTANT_PREFIXES.some(prefix => matchesPrefix(pathname, prefix))) {
    return 'assistant';
  }

  if (WORKSPACE_PREFIXES.some(prefix => matchesPrefix(pathname, prefix))) {
    return 'workspace';
  }

  return null;
};

export const readSidebarMode = (tenantId: string | null | undefined): SidebarMode | null => {
  if (!tenantId) {
    return null;
  }

  try {
    const value = localStorage.getItem(`${STORAGE_PREFIX}${tenantId}`);
    return value === 'assistant' || value === 'workspace' ? value : null;
  } catch {
    return null;
  }
};

export const writeSidebarMode = (tenantId: string | null | undefined, mode: SidebarMode): void => {
  if (!tenantId) {
    return;
  }

  try {
    localStorage.setItem(`${STORAGE_PREFIX}${tenantId}`, mode);
  } catch {
    // The choice still applies for this visit when storage is blocked.
  }
};

export const resolveSidebarMode = ({
  pathname,
  stored,
  assistantAvailable,
  workspaceAvailable,
}: {
  pathname: string;
  stored: SidebarMode | null;
  assistantAvailable: boolean;
  workspaceAvailable: boolean;
}): SidebarMode => {
  if (assistantAvailable && !workspaceAvailable) {
    return 'assistant';
  }

  if (workspaceAvailable && !assistantAvailable) {
    return 'workspace';
  }

  const fromPath = sidebarModeForPath(pathname);
  if (fromPath) {
    return fromPath;
  }

  return stored ?? 'assistant';
};
